import React, { useState } from "react"
import { Fade } from "react-reveal"
import { Flex, Box, Text } from "rebass"
import { Helmet } from "react-helmet"
import { withPrefix } from "gatsby"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faUsers,
  faBriefcase,
  faStar,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons"

import { Link } from "gatsby"
import { CoverLayout } from "../components/Layout"
import Section from "../components/Section"
import CenterHeader from "../components/CenterHeader"
import HoverShadowBox from "../components/HoverShadowBox"
import Accordion from "../components/Faq"

import styles from "./recruitment.module.css"

import nycVideo from "../images/Website_05.mp4"

const Band = ({ compact, ...props }) => (
  <div
    className={styles.band}
    style={{ padding: compact ? "16px 20px 20px" : "32px 24px 40px" }}
  >
    <div className={styles.bandContent}>{props.children}</div>
  </div>
)

const Divider = () => (
  <hr
    className={styles.recruitmentDivider}
    style={{
      border: 0,
      height: "1px",
      margin: "28px auto",
      width: "80%",
    }}
  />
)

const CardTitle = (props) => (
  <Text
    fontSize={[18, 20]}
    fontWeight="bold"
    fontFamily="Georgia, serif"
    letterSpacing="0.02em"
    sx={{ marginBottom: 10 }}
  >
    {props.children}
  </Text>
)

const CardLine = (props) => (
  <Text fontSize={[15, 16]} fontFamily="Heebo" sx={{ opacity: 0.95 }}>
    {props.children}
  </Text>
)

const FlipScheduleCard = ({
  title,
  date,
  time,
  location,
  attire,
  note,
  description,
  isFlipped,
  onFlip,
}) => {
  const [month, day] = date.split(" ")
  return (
    <div className={styles.scheduleCardWrap}>
      <div
        className={`${styles.scheduleCard} ${isFlipped ? styles.flipped : ""}`}
        onClick={onFlip}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onFlip()}
        role="button"
        tabIndex={0}
        aria-label={isFlipped ? `Flip back: ${title}` : `Flip to read: ${title}`}
      >
        <div className={styles.scheduleCardInner}>
          <div className={styles.scheduleCardFront}>
            <div className={styles.cardLeft}>
              <h3 className={styles.cardTitle}>{title}</h3>
              {note ? (
                <div className={styles.cardTitleNote}>* {note}</div>
              ) : null}
              <div className={styles.dateDisplay}>
                <span className={styles.dateMonth}>{month}</span>
                <span className={styles.dateDay}>{day}</span>
              </div>
            </div>
            <div className={styles.cardRight}>
              <div className={styles.cardRightLine}>{time}</div>
              <div className={styles.cardRightLine}>{location}</div>
              <div className={styles.cardRightLine}>{attire}</div>
            </div>
          </div>
          <div className={styles.scheduleCardBack}>
            <div className={styles.backContent}>
              <p className={styles.backText}>{description}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const SCHEDULE_EVENTS = [
  {
    title: "Meet the Chapter",
    date: "September 28",
    time: "7-9 PM",
    location: "Paul Merage SB1120",
    attire: "Casual Attire",
    note: null,
    icon: faUsers,
    description:
      "Come out to learn more about DSP, meet our brothers, and connect in a relaxed setting. Get a feel for our brotherhood and see what Pi Sigma is all about.",
  },
  {
    title: "Professional Night",
    date: "September 30",
    time: "7-9 PM",
    location: "Paul Merage SB1128",
    attire: "Business Professional Attire",
    note: null,
    icon: faBriefcase,
    description:
      "Learn about the professional resources our chapter has to offer. Come in business professional attire with your resume, cover letter, and any materials you’d like our brothers to review and give feedback on.",
  },
  {
    title: "Social Night",
    date: "October 4",
    time: "7-10 PM",
    location: "Corona Del Mar",
    attire: "Casual Attire",
    note: null,
    icon: faUsers,
    description:
      "Join us at the beach for a fun, social setting. There will be a fundraiser at Cha at UTC beforehand from 4-6 PM, where you can socialize with brothers and get rides from there. Check for updates on ride arrangements!",
  },
  {
    title: "Invite-Only Event",
    date: "October 6",
    time: "7-9 PM",
    location: "TBA",
    attire: "Cocktail Attire",
    note: "Invite Only",
    icon: faStar,
    description: "Invite only. More details will be sent via email.",
  },
  {
    title: "Interviews",
    date: "October 8",
    time: "TBA",
    location: "TBA",
    attire: "Business Professional Attire",
    note: "Invite Only",
    icon: faUserTie,
    description: "Invite only. More details will be sent via email.",
  },
]

const RecruitmentScheduleBlock = () => {
  const [flippedIndex, setFlippedIndex] = useState(null)

  return (
    <Band compact>
      <Fade bottom distance="16px" duration={500}>
        <div className={styles.scheduleHeader}>
          <CenterHeader marginTop={4} marginBottom={8}>
            Recruitment Schedule
          </CenterHeader>
        </div>
      </Fade>
      <Fade bottom distance="20px" duration={500} delay={80}>
        <Text
          fontSize={[14, 15]}
          fontFamily="Heebo"
          sx={{
            maxWidth: 560,
            margin: "0 auto 16px",
            textAlign: "center",
            opacity: 0.95,
          }}
        >
          Plan your week around recruitment. Each event highlights a different
          side of Pi Sigma—from first impressions to invite-only conversations.
        </Text>
      </Fade>
      <div className={styles.scheduleTimelineWrap}>
        <div className={styles.scheduleTimelineTrack} aria-hidden="true" />
        <div className={styles.scheduleList}>
          {SCHEDULE_EVENTS.map((event, index) => (
            <div
              key={index}
              className={styles.scheduleItemRow}
            >
              <div className={styles.scheduleNodeCol}>
                <FontAwesomeIcon
                  icon={event.icon}
                  className={styles.scheduleIcon}
                  aria-hidden="true"
                />
              </div>
              <div className={styles.scheduleCardCol}>
                <FlipScheduleCard
                  {...event}
                  isFlipped={flippedIndex === index}
                  onFlip={() =>
                    setFlippedIndex((prev) => (prev === index ? null : index))
                  }
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Band>
  )
}

export default () => (
  <CoverLayout
    navbarTextColor="white"
    coverElement={
      <div className={styles.recruitmentHeroWrap}>
        <video
          className={styles.heroVideo}
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
          poster={withPrefix("/images/nyc_landscape_hero.png")}
        >
          <source src={nycVideo} type="video/mp4" />
        </video>
        <div
          className={styles.recruitmentHeroContent}
          style={{
            position: "relative",
            width: "100%",
            maxWidth: 1120,
            display: "flex",
            flexDirection: "column",
            gap: 20,
            padding: "120px 24px 80px",
          }}
        >
          <Text
            className={styles.recruitmentHeroKicker}
            fontSize={[13, 14]}
            fontFamily="Heebo, -apple-system, BlinkMacSystemFont, system-ui, sans-serif"
            sx={{
              textTransform: "uppercase",
              letterSpacing: "0.3em",
              color: "#ffffff",
            }}
          >
            Delta Sigma Pi · Pi Sigma
          </Text>
          <h1 className={styles.recruitmentHeroTitle} style={{ fontSize: "clamp(36px, 5vw, 56px)", textTransform: "uppercase", margin: 0 }}>
            Fall Recruitment
          </h1>
          <h2
            className={styles.recruitmentHeroSubtitle}
            style={{
              fontFamily: "Georgia, serif",
              fontWeight: 500,
              fontSize: "clamp(22px, 3vw, 30px)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              margin: 0,
              color: "#ffffff",
            }}
          >
            The Best Fit For Your Future
          </h2>
          <p
            style={{
              maxWidth: 640,
              lineHeight: 1.7,
              fontSize: 16,
              marginTop: 10,
              marginBottom: 24,
              color: "#ffffff",
            }}
          >
            Every dream starts somewhere. This fall, start yours with DSP. Join us for our Fall 2026 recruitment and discover the opportunities, connections, and people waiting for you.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
            <Link
              to="/recruitment#schedule"
              className={`${styles.viewScheduleBtn} ${styles.heroViewScheduleBtn}`}
            >
              View Schedule
            </Link>
          </div>
        </div>
      </div>
    }
  >
    <div className={styles.recruitmentPageBg} aria-hidden="true" />
    <div className={styles.recruitmentPageContent}>
    <Helmet>
      <title>Recruitment | Delta Sigma Pi - Pi Sigma Chapter</title>
      <meta
        name="Delta Sigma Pi - Pi Sigma Chapter Recruitment Page"
        content="Delta Sigma Pi - Pi Sigma Chapter Recruitment Page"
      />
    </Helmet>

      <Divider />
      <Fade bottom distance="40px">
        <div
          id ="schedule"
          style={{ scrollMarginTop: 100, marginTop: 72 }}
        />
        <RecruitmentScheduleBlock />
      </Fade>

  

    <Divider />

    <Fade bottom distance="40px">
      <Band>
        <CenterHeader marginTop={10} marginBottom={35}>
          What to Expect
        </CenterHeader>
        <Flex flexWrap="wrap" justifyContent="center" marginBottom="-20px">
          <Box width={[1, 1 / 2, 1 / 2, 1 / 2]}>
            <HoverShadowBox raise>
              <CardTitle>Meet Our Brothers</CardTitle>
              <Text
                fontSize={[15, 16]}
                fontFamily="Heebo"
                sx={{ opacity: 0.95 }}
              >
                See what makes Pi Sigma feel like home. Come curious, motivated, and ready to connect.
              </Text>
            </HoverShadowBox>
          </Box>
          <Box width={[1, 1 / 2, 1 / 2, 1 / 2]}>
            <HoverShadowBox raise>
              <CardTitle>Professional Opportunities</CardTitle>
              <Text
                fontSize={[15, 16]}
                fontFamily="Heebo"
                sx={{ opacity: 0.95 }}
              >
                Learn how DSP supports recruiting, skill-building, and
                mentorship across industries—plus how our alumni network can
                open doors long after graduation.
              </Text>
            </HoverShadowBox>
          </Box>
          <Box width={[1, 1 / 2, 1 / 2, 1 / 2]}>
            <HoverShadowBox raise>
              <CardTitle>Build Real Connections</CardTitle>
              <Text
                fontSize={[15, 16]}
                fontFamily="Heebo"
                sx={{ opacity: 0.95 }}
              >
                Recruitment is designed to be social and welcoming. You’ll meet
                other candidates, talk 1:1 with brothers, and get a feel for the
                support system you’d be joining.
              </Text>
            </HoverShadowBox>
          </Box>
          <Box width={[1, 1 / 2, 1 / 2, 1 / 2]}>
            <HoverShadowBox raise>
              <CardTitle>Understand the Process</CardTitle>
              <Text
                fontSize={[15, 16]}
                fontFamily="Heebo"
                sx={{ opacity: 0.95 }}
              >
                We’ll walk you through what we look for, how invite-only events
                work, and how to put your best foot forward.
              </Text>
            </HoverShadowBox>
          </Box>
        </Flex>
      </Band>
    </Fade>
    
    <div style={{ margin: "60px auto", maxWidth: 800 }}>
      <hr
        style={{
          border: 0,
          height: "1px",
          background: "rgba(0, 0, 0, 0.18)",
          width: "65%",
          margin: "0 auto",
        }}
      />
    </div>

    <Fade bottom distance="40px">
      <Band>
        <CenterHeader marginTop={10} marginBottom={30}>
          FAQ's
        </CenterHeader>
        <div className={styles.faqList}>
        <Accordion
          title="Are all events required?"
          content="
            While we encourage you to attend all three open recruitment events so you can fully get to know our brothers and the chapter, attendance at at least two of the three events is required. Applications will not be considered unless you attend a minimum of two events.
          "
        />
        <Accordion
          title="Do I need prior business experience?"
          content="
            No prior business experience is required. We evaluate prospective members on a holistic basis, so your professional background is only one part of the picture. If you have experience, that’s great. If you don’t, recruitment is designed to help you learn more about DSP and grow professionally.
          "
        />
        <Accordion
          title="What should I wear?"
          content={
            "Each event has a different dress code.\n\n" +
            "Meet the Chapter: Casual\n" +
            "Professional Night: Business Professional\n" +
            "Social Night: Casual\n" +
            "Mocktail Night: Cocktail Attire\n" +
            "Interviews: Business Professional"
          }
        />
        <Accordion
          title="How do invite-only events work?"
          content="
            After attending the first three recruitment events, you will receive an email letting you know whether you have been selected to move forward to the next round (Mocktail Night). If selected, additional details will be provided. The same process applies for interviews, which follow Mocktail Night.
          "
        />
        </div>
      </Band>
    </Fade>
    </div>
  </CoverLayout>
)

